import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { useAuth } from '../../contexts/AuthContext';
import { Button } from '../../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import { Badge } from '../../components/ui/badge';
import { Plus, Pencil, Trash2, X, Save, Image } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const AdminMotorhomes = () => {
  const { getAuthHeader } = useAuth();
  const [motorhomes, setMotorhomes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editingId, setEditingId] = useState(null);
  const [showForm, setShowForm] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    type: 'manual',
    berth: 4,
    slug: '',
    description: '',
    features: '',
    pricePerNight: 100,
    images: '',
    available: true,
    bookingUnitId: '',
    bookingCategoryId: '49050',
    specifications: {
      length: '',
      width: '',
      height: '',
      transmission: 'Manual',
      fuelType: 'Diesel',
      sleeps: 4,
      seatbelts: 4
    }
  });

  useEffect(() => {
    fetchMotorhomes();
  }, []);

  const fetchMotorhomes = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/admin/motorhomes`, getAuthHeader());
      setMotorhomes(response.data);
    } catch (error) {
      console.error('Error fetching motorhomes:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const data = {
        ...formData,
        features: formData.features.split('\n').filter(f => f.trim()),
        images: formData.images.split('\n').filter(i => i.trim())
      };

      if (editingId) {
        await axios.put(`${API_URL}/api/admin/motorhomes/${editingId}`, data, getAuthHeader());
      } else {
        await axios.post(`${API_URL}/api/admin/motorhomes`, data, getAuthHeader());
      }
      
      fetchMotorhomes();
      resetForm();
    } catch (error) {
      console.error('Error saving motorhome:', error);
      alert('Error saving motorhome');
    }
  };

  const handleEdit = (motorhome) => {
    setFormData({
      ...motorhome,
      features: motorhome.features.join('\n'),
      images: motorhome.images.join('\n')
    });
    setEditingId(motorhome.id);
    setShowForm(true);
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Are you sure you want to delete this motorhome?')) return;
    try {
      await axios.delete(`${API_URL}/api/admin/motorhomes/${id}`, getAuthHeader());
      fetchMotorhomes();
    } catch (error) {
      console.error('Error deleting motorhome:', error);
    }
  };

  const resetForm = () => {
    setFormData({
      name: '',
      type: 'manual',
      berth: 4,
      slug: '',
      description: '',
      features: '',
      pricePerNight: 100,
      images: '',
      available: true,
      bookingUnitId: '',
      bookingCategoryId: '49050',
      specifications: {
        length: '',
        width: '',
        height: '',
        transmission: 'Manual',
        fuelType: 'Diesel',
        sleeps: 4,
        seatbelts: 4
      }
    });
    setEditingId(null);
    setShowForm(false);
  };

  if (loading) {
    return <div className="flex justify-center p-8"><div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div></div>;
  }

  return (
    <div>
      <div className="flex justify-between items-center mb-6">
        <div>
          <h1 className="text-3xl font-bold text-slate-900">Motorhomes</h1>
          <p className="text-slate-600">Manage your motorhome fleet</p>
        </div>
        <Button onClick={() => setShowForm(true)} data-testid="add-motorhome-btn">
          <Plus className="h-4 w-4 mr-2" /> Add Motorhome
        </Button>
      </div>

      {showForm && (
        <Card className="mb-6">
          <CardHeader className="flex flex-row items-center justify-between">
            <CardTitle>{editingId ? 'Edit Motorhome' : 'Add New Motorhome'}</CardTitle>
            <Button variant="ghost" size="sm" onClick={resetForm}><X className="h-4 w-4" /></Button>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                <div>
                  <Label>Name</Label>
                  <Input value={formData.name} onChange={(e) => setFormData({...formData, name: e.target.value})} required />
                </div>
                <div>
                  <Label>Slug (URL)</Label>
                  <Input value={formData.slug} onChange={(e) => setFormData({...formData, slug: e.target.value})} placeholder="roller-team-740" required />
                </div>
                <div>
                  <Label>Type</Label>
                  <select className="w-full border rounded-md p-2" value={formData.type} onChange={(e) => setFormData({...formData, type: e.target.value})}>
                    <option value="manual">Manual</option>
                    <option value="automatic">Automatic</option>
                  </select>
                </div>
                <div>
                  <Label>Berth</Label>
                  <Input type="number" value={formData.berth} onChange={(e) => setFormData({...formData, berth: parseInt(e.target.value)})} required />
                </div>
                <div>
                  <Label>Price Per Night (£)</Label>
                  <Input type="number" value={formData.pricePerNight} onChange={(e) => setFormData({...formData, pricePerNight: parseInt(e.target.value)})} required />
                </div>
                <div>
                  <Label>Available</Label>
                  <select className="w-full border rounded-md p-2" value={formData.available} onChange={(e) => setFormData({...formData, available: e.target.value === 'true'})}>
                    <option value="true">Yes</option>
                    <option value="false">No</option>
                  </select>
                </div>
              </div>

              <div>
                <Label>Description</Label>
                <Textarea value={formData.description} onChange={(e) => setFormData({...formData, description: e.target.value})} rows={3} required />
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <Label>Features (one per line)</Label>
                  <Textarea value={formData.features} onChange={(e) => setFormData({...formData, features: e.target.value})} rows={4} placeholder="Fixed rear double bed&#10;Electric drop down bed&#10;4 travelling belts" />
                </div>
                <div>
                  <Label>Image URLs (one per line)</Label>
                  <Textarea value={formData.images} onChange={(e) => setFormData({...formData, images: e.target.value})} rows={4} placeholder="https://example.com/image1.jpg&#10;https://example.com/image2.jpg" />
                </div>
              </div>

              <div className="border-t pt-4">
                <h3 className="font-semibold mb-3">Specifications</h3>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                  <div>
                    <Label>Length</Label>
                    <Input value={formData.specifications.length} onChange={(e) => setFormData({...formData, specifications: {...formData.specifications, length: e.target.value}})} placeholder="7.4m" />
                  </div>
                  <div>
                    <Label>Width</Label>
                    <Input value={formData.specifications.width} onChange={(e) => setFormData({...formData, specifications: {...formData.specifications, width: e.target.value}})} placeholder="2.3m" />
                  </div>
                  <div>
                    <Label>Height</Label>
                    <Input value={formData.specifications.height} onChange={(e) => setFormData({...formData, specifications: {...formData.specifications, height: e.target.value}})} placeholder="3.1m" />
                  </div>
                  <div>
                    <Label>Transmission</Label>
                    <select className="w-full border rounded-md p-2" value={formData.specifications.transmission} onChange={(e) => setFormData({...formData, specifications: {...formData.specifications, transmission: e.target.value}})}>
                      <option value="Manual">Manual</option>
                      <option value="Automatic">Automatic</option>
                    </select>
                  </div>
                  <div>
                    <Label>Fuel Type</Label>
                    <Input value={formData.specifications.fuelType} onChange={(e) => setFormData({...formData, specifications: {...formData.specifications, fuelType: e.target.value}})} placeholder="Diesel" />
                  </div>
                  <div>
                    <Label>Sleeps</Label>
                    <Input type="number" value={formData.specifications.sleeps} onChange={(e) => setFormData({...formData, specifications: {...formData.specifications, sleeps: parseInt(e.target.value)}})} />
                  </div>
                  <div>
                    <Label>Seat Belts</Label>
                    <Input type="number" value={formData.specifications.seatbelts || ''} onChange={(e) => setFormData({...formData, specifications: {...formData.specifications, seatbelts: parseInt(e.target.value) || null}})} />
                  </div>
                </div>
              </div>

              <div className="border-t pt-4">
                <h3 className="font-semibold mb-3">Booking Calendar (Anytime Booking)</h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <Label>Unit ID</Label>
                    <Input value={formData.bookingUnitId || ''} onChange={(e) => setFormData({...formData, bookingUnitId: e.target.value})} placeholder="50253" />
                  </div>
                  <div>
                    <Label>Category ID</Label>
                    <Input value={formData.bookingCategoryId || ''} onChange={(e) => setFormData({...formData, bookingCategoryId: e.target.value})} placeholder="49050" />
                  </div>
                </div>
              </div>

              <div className="flex gap-2">
                <Button type="submit"><Save className="h-4 w-4 mr-2" /> Save Motorhome</Button>
                <Button type="button" variant="outline" onClick={resetForm}>Cancel</Button>
              </div>
            </form>
          </CardContent>
        </Card>
      )}

      <div className="grid gap-4">
        {motorhomes.map((motorhome) => (
          <Card key={motorhome.id}>
            <CardContent className="p-4">
              <div className="flex items-start gap-4">
                {motorhome.images?.[0] && (
                  <img src={motorhome.images[0]} alt={motorhome.name} className="w-32 h-24 object-cover rounded-lg" />
                )}
                <div className="flex-1">
                  <div className="flex items-center gap-2 mb-1">
                    <h3 className="font-semibold text-lg">{motorhome.name}</h3>
                    <Badge variant={motorhome.available ? 'default' : 'secondary'}>
                      {motorhome.available ? 'Available' : 'Unavailable'}
                    </Badge>
                    <Badge variant="outline">{motorhome.type}</Badge>
                    <Badge variant="outline">{motorhome.berth} Berth</Badge>
                  </div>
                  <p className="text-slate-600 text-sm line-clamp-2">{motorhome.description}</p>
                  <div className="flex items-center gap-4 mt-2 text-sm text-slate-500">
                    <span>£{motorhome.pricePerNight}/night</span>
                    <span>{motorhome.images?.length || 0} images</span>
                    {motorhome.bookingUnitId && <span>Booking ID: {motorhome.bookingUnitId}</span>}
                  </div>
                </div>
                <div className="flex gap-2">
                  <Button variant="outline" size="sm" onClick={() => handleEdit(motorhome)}>
                    <Pencil className="h-4 w-4" />
                  </Button>
                  <Button variant="outline" size="sm" className="text-red-600 hover:text-red-700" onClick={() => handleDelete(motorhome.id)}>
                    <Trash2 className="h-4 w-4" />
                  </Button>
                </div>
              </div>
            </CardContent>
          </Card>
        ))}
      </div>
    </div>
  );
};

export default AdminMotorhomes;
